<template>
    <div class="tinymce-editor-container">
        <!-- 使用一個隱藏的 textarea 作為 TinyMCE 初始化的目標 -->
        <textarea :id="editorId"></textarea>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

// 定義元件的 props
const props = defineProps({
    modelValue: { // 用於 v-model 的值，表示編輯器內容
        type: String,
        default: '',
    },
    apiKey: { // TinyMCE 的 API Key (如果您有使用雲服務，自行架設則通常不需要)
        type: String,
        default: '',
    },
    init: { // TinyMCE 的初始化配置物件
        type: Object,
        default: () => ({}),
    },
    disabled: { // 是否禁用編輯器
        type: Boolean,
        default: false,
    },
    height: {
        type: [String, Number],
        default: 200,
    }
});

// 定義元件發出的事件
const emit = defineEmits(['update:modelValue', 'ready', 'change']);

const editorId = ref(`tinymce-${Math.random().toString(36).substring(2, 11)}`); // 為編輯器生成唯一 ID
let editorInstance: any = null; // 儲存 TinyMCE 編輯器實例

/**
 * 異步載入 TinyMCE 腳本
 * 這確保 TinyMCE 在需要時才載入，並且不會阻礙頁面渲染
 */
const loadTinyMCEScript = () => {
    return new Promise((resolve, reject) => {
        // 檢查 TinyMCE 是否已經載入
        if (typeof window.tinymce !== 'undefined') {
            resolve(window.tinymce);
            return;
        }

        const script = document.createElement('script');
        script.src = '/tinymce/tinymce.min.js'; // 從 public/tinymce 路徑載入
        script.onload = () => resolve(window.tinymce);
        script.onerror = (error) => reject(error);
        document.head.appendChild(script);
    });
};

/**
 * 初始化 TinyMCE 編輯器
 */
const initEditor = async () => {
    try {
        const tinymce = await loadTinyMCEScript();

        // 完整的 TinyMCE 配置選項
        const defaultInitOptions = {
            base_url: '/tinymce', // TinyMCE 的基礎路徑
            selector: `#${editorId.value}`, // 選擇器指向 textarea 的 ID
            language: 'zh-Hans', // 預設使用簡體中文，如果您需要繁體中文，請確認 TinyMCE 是否有提供繁體語言包
            // 如果沒有提供繁體中文，可以嘗試使用 'zh-Hans' 或 'en'
            height: props.height,
            menubar: false,
            plugins: "preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons accordion",
            // ✨ 已新增 'link' 和 'image' 到工具列 ✨
            toolbar: "fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image | table media | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code ",

            skin: 'oxide', // 預設皮膚
            content_css: 'default', // 預設內容 CSS
            font_family_formats: '微軟正黑體=Microsoft JhengHei; 標楷體=DFKai-SB; 新細明體=MingLiU; Arial=Arial,Helvetica,sans-serif; Times New Roman=Times New Roman',
            // 定義自定義的圖像上傳器 (如果需要)
            images_upload_handler: (blobInfo: any, progress: any) =>
                new Promise((resolve, reject) => {
                    // 這裡可以實現圖像上傳邏輯
                    // 例如，將 blobInfo.blob() 發送到您的伺服器
                    // 為了範例，我們只返回一個 placeholder URL
                    setTimeout(() => {
                        resolve('https://placehold.co/150x150/FFF/000?text=Uploaded+Image');
                    }, 1000);
                }),
            // 事件處理
            setup: (editor: any) => {
                editor.on('init', () => {
                    editor.setContent(props.modelValue); // 初始化時設定內容
                    emit('ready', editor);
                    editorInstance = editor; // 儲存實例
                    if (props.disabled) {
                        editor.setMode('readonly');
                    }
                });
                editor.on('change input undo redo', () => {
                    emit('update:modelValue', editor.getContent()); // 內容改變時更新 v-model
                    emit('change', editor.getContent());
                });
            },
            ...props.init, // 合併使用者傳入的 init 配置，允許覆蓋預設值
        };

        tinymce.init(defaultInitOptions);
    } catch (error) {
        console.error('TinyMCE 初始化失敗:', error);
    }
};

// 元件掛載時初始化編輯器
onMounted(() => {
    initEditor();
});

// 元件卸載前銷毀編輯器實例，防止記憶體洩漏
onBeforeUnmount(() => {
    if (editorInstance) {
        editorInstance.destroy();
    }
});

// 監聽 modelValue 的變化，更新編輯器內容
watch(() => props.modelValue, (newValue) => {
    if (editorInstance && editorInstance.getContent() !== newValue) {
        editorInstance.setContent(newValue);
    }
});

// 監聽 disabled prop 的變化，切換編輯器模式
watch(() => props.disabled, (newValue) => {
    if (editorInstance) {
        editorInstance.setMode(newValue ? 'readonly' : 'design');
    }
});
</script>

<style scoped>
/* 可以針對 TinyMCE 容器進行樣式調整 */
.tinymce-editor-container {
    margin-top: 20px;
}
</style>
